import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    App,
    Button,
    Card,
    DatePicker,
    Input,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import { EyeOutlined, NodeIndexOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import { listWorkItems } from "../../../api/v3/mailHubApi";
import {
    formatMailHubError,
    readMailHubData,
    validateWorkItemListPayload,
} from "../lib/contracts";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const ITEM_TYPE_OPTIONS = [
    "email_store",
    "email_classify",
    "attachment_transfer",
    "pdf_ocr_extract",
    "business_judgment",
    "mvt_parse",
    "cpm_parse",
    "ffm_parse",
    "fwb_parse",
    "invoice_extract",
    "rich_render",
].map((value) => ({ label: value, value }));

const STATUS_OPTIONS = [
    { label: "pending", value: "pending" },
    { label: "in_progress", value: "in_progress" },
    { label: "dispatched", value: "dispatched" },
    { label: "completed", value: "completed" },
    { label: "failed", value: "failed" },
    { label: "dead", value: "dead" },
    { label: "cancelled", value: "cancelled" },
];

const TIME_FIELD_OPTIONS = [
    { label: "创建时间", value: "created_at" },
    { label: "更新时间", value: "updated_at" },
    { label: "完成时间", value: "completed_at" },
];

function statusColor(status) {
    const map = {
        pending: "warning",
        in_progress: "processing",
        dispatched: "blue",
        completed: "success",
        failed: "error",
        dead: "red",
        cancelled: "default",
    };
    return map[status];
}

function shortId(value) {
    if (!value) return "—";
    return value.length > 12 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

function formatTime(value) {
    if (!value) return <Text type="secondary">—</Text>;
    return (
        <div>
            <div>{dayjs(value).format("YYYY-MM-DD HH:mm:ss")}</div>
            <Text type="secondary">{dayjs(value).fromNow()}</Text>
        </div>
    );
}

function serializeTimeRange(range) {
    if (!Array.isArray(range) || range.length !== 2) {
        return { timeFrom: undefined, timeTo: undefined };
    }
    return {
        timeFrom: range[0]?.toISOString(),
        timeTo: range[1]?.toISOString(),
    };
}

const WorkItemListPage = () => {
    const navigate = useNavigate();
    const { message } = App.useApp();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
    const [loadError, setLoadError] = useState(null);
    const [textFilters, setTextFilters] = useState({
        targetId: "",
        mailboxReceiptId: "",
        resultSummary: "",
    });
    const [filters, setFilters] = useState({
        itemType: undefined,
        status: undefined,
        targetId: "",
        mailboxReceiptId: "",
        resultSummary: "",
        timeField: "created_at",
        timeRange: null,
        page: 1,
        pageSize: 20,
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const timeRange = serializeTimeRange(filters.timeRange);
            const response = await listWorkItems({
                itemType: filters.itemType,
                status: filters.status,
                targetId: filters.targetId?.trim() || undefined,
                mailboxReceiptId: filters.mailboxReceiptId?.trim() || undefined,
                resultSummary: filters.resultSummary?.trim() || undefined,
                timeField: filters.timeRange ? filters.timeField : undefined,
                timeFrom: timeRange.timeFrom,
                timeTo: timeRange.timeTo,
                page: filters.page,
                pageSize: filters.pageSize,
            });
            const data = validateWorkItemListPayload(readMailHubData(response, "Work Item 列表"));
            setItems(data.items);
            setPagination(data.pagination);
            setLoadError(null);
        } catch (error) {
            const errorMessage = formatMailHubError(error, "加载 Work Item 失败");
            setItems([]);
            setPagination({ page: 1, pageSize: filters.pageSize, total: 0 });
            setLoadError(errorMessage);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [filters, message]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const updateFilter = useCallback((patch) => {
        setFilters((current) => ({ ...current, ...patch, page: 1 }));
    }, []);

    const applyTextFilters = useCallback((patch = {}) => {
        const nextTextFilters = { ...textFilters, ...patch };
        setTextFilters(nextTextFilters);
        updateFilter(nextTextFilters);
    }, [textFilters, updateFilter]);

    const columns = useMemo(() => [
        {
            title: "Work Item",
            key: "work_item",
            width: 260,
            fixed: "left",
            render: (_, record) => {
                const wi = record.work_item;
                return (
                    <div>
                        <Button
                            type="link"
                            style={{ padding: 0, height: "auto", fontWeight: 600 }}
                            onClick={() => navigate(`/mail-hub/ops/work-items/${wi.id}`)}
                        >
                            {wi.item_type}
                        </Button>
                        <div>
                            <Tooltip title={wi.id}>
                                <Text type="secondary" style={{ fontSize: 12 }}>{shortId(wi.id)}</Text>
                            </Tooltip>
                        </div>
                        {wi.transition_id ? <Tag color="geekblue">{wi.transition_id}</Tag> : null}
                    </div>
                );
            },
        },
        {
            title: "状态",
            key: "status",
            width: 150,
            render: (_, record) => {
                const wi = record.work_item;
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={statusColor(wi.status)}>{wi.status}</Tag>
                        <Text type="secondary">{wi.execution_kind || "—"}{wi.pool_profile ? ` / ${wi.pool_profile}` : ""}</Text>
                    </Space>
                );
            },
        },
        {
            title: "重试",
            key: "retry",
            width: 100,
            render: (_, record) => {
                const wi = record.work_item;
                const exhausted = wi.max_retries > 0 && wi.retry_count >= wi.max_retries;
                return <Tag color={exhausted ? "red" : "default"}>{wi.retry_count} / {wi.max_retries}</Tag>;
            },
        },
        {
            title: "目标",
            key: "target",
            width: 260,
            render: (_, record) => {
                const wi = record.work_item;
                const receiptId = record.receipt_summary.id;
                return (
                    <Space direction="vertical" size={2}>
                        <Tag>{wi.target_type}</Tag>
                        <Tooltip title={wi.target_id}>
                            <Text copyable style={{ fontSize: 12 }}>{shortId(wi.target_id)}</Text>
                        </Tooltip>
                        {receiptId ? (
                            <Text type="secondary" copyable={{ text: receiptId }} style={{ fontSize: 12 }}>
                                Receipt {shortId(receiptId)}
                            </Text>
                        ) : null}
                    </Space>
                );
            },
        },
        {
            title: "邮件摘要",
            key: "message",
            width: 320,
            render: (_, record) => {
                const messageSummary = record.canonical_message_summary;
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{messageSummary.subject || "(无主题)"}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {messageSummary.from_display ? `${messageSummary.from_display} <${messageSummary.from_address}>` : messageSummary.from_address}
                        </Text>
                        {messageSummary.date ? <div><Text type="secondary" style={{ fontSize: 12 }}>{dayjs(messageSummary.date).format("YYYY-MM-DD HH:mm")}</Text></div> : null}
                    </div>
                );
            },
        },
        {
            title: "租约",
            key: "lock",
            width: 220,
            render: (_, record) => {
                const wi = record.work_item;
                if (!wi.locked_by && !wi.locked_at) return <Text type="secondary">—</Text>;
                return (
                    <Space direction="vertical" size={2}>
                        <Text>{wi.locked_by || "未记录持有者"}</Text>
                        {formatTime(wi.locked_at)}
                    </Space>
                );
            },
        },
        {
            title: "结果摘要",
            key: "result_summary",
            width: 320,
            render: (_, record) => record.work_item.result_summary ? (
                <Tooltip title={record.work_item.result_summary}>
                    <Text>{record.work_item.result_summary.length > 120 ? `${record.work_item.result_summary.slice(0, 120)}...` : record.work_item.result_summary}</Text>
                </Tooltip>
            ) : <Text type="secondary">—</Text>,
        },
        {
            title: "创建 / 更新 / 完成",
            key: "times",
            width: 260,
            render: (_, record) => {
                const wi = record.work_item;
                return (
                    <Space direction="vertical" size={6}>
                        <div><Text type="secondary">创建 </Text>{dayjs(wi.created_at).format("YYYY-MM-DD HH:mm:ss")}</div>
                        <div><Text type="secondary">更新 </Text>{dayjs(wi.updated_at).format("YYYY-MM-DD HH:mm:ss")}</div>
                        <div><Text type="secondary">完成 </Text>{wi.completed_at ? dayjs(wi.completed_at).format("YYYY-MM-DD HH:mm:ss") : "—"}</div>
                    </Space>
                );
            },
        },
        {
            title: "操作",
            key: "actions",
            width: 190,
            fixed: "right",
            render: (_, record) => {
                const wi = record.work_item;
                return (
                    <Space>
                        <Button icon={<EyeOutlined />} onClick={() => navigate(`/mail-hub/ops/work-items/${wi.id}`)}>
                            查看
                        </Button>
                    </Space>
                );
            },
        },
    ], [navigate]);

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                    <NodeIndexOutlined style={{ marginRight: 8 }} />
                    Work Item 查询
                </Title>
                <Space>
                    <Button
                        onClick={() => {
                            setTextFilters({ targetId: "", mailboxReceiptId: "", resultSummary: "" });
                            setFilters({
                                itemType: undefined,
                                status: undefined,
                                targetId: "",
                                mailboxReceiptId: "",
                                resultSummary: "",
                                timeField: "created_at",
                                timeRange: null,
                                page: 1,
                                pageSize: 20,
                            });
                        }}
                    >
                        重置
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                        刷新
                    </Button>
                </Space>
            </div>

            <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap>
                    <Select
                        allowClear
                        showSearch
                        placeholder="item_type"
                        style={{ width: 220 }}
                        options={ITEM_TYPE_OPTIONS}
                        value={filters.itemType}
                        onChange={(value) => updateFilter({ itemType: value })}
                    />
                    <Select
                        allowClear
                        placeholder="status"
                        style={{ width: 170 }}
                        options={STATUS_OPTIONS}
                        value={filters.status}
                        onChange={(value) => updateFilter({ status: value })}
                    />
                    <Select
                        style={{ width: 140 }}
                        options={TIME_FIELD_OPTIONS}
                        value={filters.timeField}
                        onChange={(value) => updateFilter({ timeField: value })}
                    />
                    <RangePicker
                        showTime
                        style={{ width: 360 }}
                        value={filters.timeRange}
                        onChange={(value) => updateFilter({ timeRange: value })}
                    />
                    <Input
                        allowClear
                        placeholder="target_id"
                        style={{ width: 260 }}
                        value={textFilters.targetId}
                        onChange={(event) => setTextFilters((current) => ({ ...current, targetId: event.target.value }))}
                        onPressEnter={() => applyTextFilters({ targetId: textFilters.targetId })}
                    />
                    <Input
                        allowClear
                        placeholder="mailbox_receipt_id"
                        style={{ width: 260 }}
                        value={textFilters.mailboxReceiptId}
                        onChange={(event) => setTextFilters((current) => ({ ...current, mailboxReceiptId: event.target.value }))}
                        onPressEnter={() => applyTextFilters({ mailboxReceiptId: textFilters.mailboxReceiptId })}
                    />
                    <Input.Search
                        allowClear
                        enterButton={<SearchOutlined />}
                        placeholder="result_summary 包含"
                        style={{ width: 300 }}
                        value={textFilters.resultSummary}
                        onChange={(event) => setTextFilters((current) => ({ ...current, resultSummary: event.target.value }))}
                        onSearch={(value) => applyTextFilters({ resultSummary: value })}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={() => applyTextFilters()}>
                        查询
                    </Button>
                </Space>
            </Card>

            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Work Item 列表不可用"
                    description={loadError}
                />
            ) : null}

            {!loadError ? (
                <Table
                    rowKey={(record) => record.work_item.id}
                    loading={loading}
                    columns={columns}
                    dataSource={items}
                    scroll={{ x: 2200 }}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条`,
                        onChange: (page, pageSize) => {
                            setFilters((current) => ({ ...current, page, pageSize }));
                        },
                    }}
                />
            ) : null}
        </div>
    );
};

export default WorkItemListPage;
